import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Bitcoin,
  Wallet,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownLeft,
  RefreshCw,
  Copy,
  CheckCircle,
  AlertCircle,
  Layers,
  Users,
  ChevronRight,
  Plus,
  History,
  PieChart,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useAuthStore } from '../../stores/auth.store';
import { formatCurrency, cn } from '../../lib/utils';
import { 
  useCryptoQuotations, 
  useCryptoWallets, 
  useCryptoPortfolio,
  useCryptoPurchases,
  useCryptoSales,
  CryptoQuotation,
} from '../../hooks/useCrypto';
import { useDefiPortfolio } from '../../hooks/useDefi';
import { CryptoBuyModal, CryptoSellModal } from '../../components/crypto';

const CRYPTO_ICONS: Record<string, string> = {
  BTC: '₿',
  ETH: 'Ξ',
  USDT: '₮',
  USDC: '$',
  SOL: '◎',
  BNB: '⬡',
};

const CRYPTO_COLORS: Record<string, string> = {
  BTC: 'from-orange-500 to-yellow-500',
  ETH: 'from-blue-500 to-purple-500',
  USDT: 'from-green-500 to-emerald-500',
  USDC: 'from-blue-400 to-blue-600',
  SOL: 'from-purple-500 to-pink-500',
  BNB: 'from-yellow-400 to-yellow-600',
};

export function BankingCrypto() {
  const navigate = useNavigate();
  const { hasCategory, user } = useAuthStore();
  const [copied, setCopied] = useState<string | null>(null);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  
  // Get virtual account ID from user
  const virtualAccountId = user?.virtualAccountId || '';
  
  // Fetch data
  const { data: quotations = [], isLoading: quotationsLoading } = useCryptoQuotations();
  const { data: wallets = [], isLoading: walletsLoading } = useCryptoWallets(virtualAccountId);
  const { portfolio, isLoading: portfolioLoading } = useCryptoPortfolio(virtualAccountId);
  const { portfolio: defiPortfolio } = useDefiPortfolio(virtualAccountId);
  const { data: recentPurchases = [] } = useCryptoPurchases(virtualAccountId, { status: 'COMPLETED' });
  const { data: recentSales = [] } = useCryptoSales(virtualAccountId);

  // Check if crypto is enabled
  if (!hasCategory('CRYPTO')) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
          <h2 className="text-xl font-semibold text-surface-100 mb-2">
            Criptomoedas não habilitadas
          </h2>
          <p className="text-surface-400">
            Esta funcionalidade não está disponível para sua conta.
            Entre em contato com o administrador.
          </p>
        </Card>
      </div>
    );
  }

  // Check if user has a virtual account
  if (!virtualAccountId) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md text-center p-8">
          <Wallet className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
          <h2 className="text-xl font-semibold text-surface-100 mb-2">
            Conta Virtual não encontrada
          </h2>
          <p className="text-surface-400">
            Você precisa de uma conta virtual para acessar as funcionalidades de criptomoedas.
            Entre em contato com o administrador.
          </p>
        </Card>
      </div>
    );
  }

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopied(address);
    setTimeout(() => setCopied(null), 2000);
  };

  const isLoading = quotationsLoading || walletsLoading || portfolioLoading;

  // Combine crypto and DeFi totals
  const totalCryptoUsd = portfolio.totalUsd + defiPortfolio.currentValue;
  const totalCryptoBrl = portfolio.totalBrl + defiPortfolio.currentValueBrl;

  // Recent operations (combine purchases and sales)
  const recentOperations = [
    ...recentPurchases.slice(0, 3).map(p => ({ ...p, operationType: 'PURCHASE' as const })),
    ...recentSales.slice(0, 3).map(s => ({ ...s, operationType: 'SALE' as const })),
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center">
            <Bitcoin className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-surface-100">Criptomoedas</h1>
            <p className="text-surface-400">Compre, venda e invista em cripto</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/banking/crypto/history')}>
            <History className="w-4 h-4 mr-2" />
            Histórico
          </Button>
          <Button variant="secondary" onClick={() => setShowSellModal(true)}>
            <ArrowUpRight className="w-4 h-4 mr-2" />
            Vender
          </Button>
          <Button onClick={() => setShowBuyModal(true)}>
            <ArrowDownLeft className="w-4 h-4 mr-2" />
            Comprar
          </Button>
        </div>
      </div>

      {/* Portfolio Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Total Portfolio Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2"
        >
          <Card className="bg-gradient-to-br from-orange-900/30 to-yellow-900/30 border-orange-500/30">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-surface-400 text-sm">Patrimônio Total em Cripto</p>
                <p className="text-4xl font-bold text-surface-100 mt-1">
                  ${totalCryptoUsd.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-sm text-surface-500 mt-1">
                  ≈ {formatCurrency(totalCryptoBrl)}
                </p>
                <div className={cn(
                  'flex items-center gap-1 text-sm mt-2',
                  portfolio.change24h >= 0 ? 'text-green-400' : 'text-red-400'
                )}>
                  {portfolio.change24h >= 0 ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                  {portfolio.change24h >= 0 ? '+' : ''}{portfolio.change24h.toFixed(2)}% (24h)
                </div>
              </div>
              <div className="flex items-center gap-2">
                <PieChart className="w-16 h-16 text-orange-400/30" />
              </div>
            </div>
            
            {/* Portfolio Breakdown */}
            {portfolio.breakdown.length > 0 && (
              <div className="mt-4 pt-4 border-t border-orange-500/20">
                <div className="flex flex-wrap gap-3">
                  {portfolio.breakdown.map((item) => (
                    <div key={item.currency} className="flex items-center gap-2 bg-surface-800/50 rounded-lg px-3 py-1.5">
                      <span className={`w-6 h-6 rounded-full bg-gradient-to-br ${CRYPTO_COLORS[item.currency] || 'from-surface-500 to-surface-600'} flex items-center justify-center text-white text-xs font-bold`}>
                        {CRYPTO_ICONS[item.currency] || item.currency[0]}
                      </span>
                      <span className="text-surface-300 text-sm">{item.currency}</span>
                      <span className="text-surface-500 text-sm">{item.percentage.toFixed(1)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </motion.div>

        {/* DeFi Summary Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="h-full bg-gradient-to-br from-purple-900/30 to-pink-900/30 border-purple-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-surface-300 font-medium">Investimentos DeFi</h3>
              <Layers className="w-5 h-5 text-purple-400" />
            </div>
            <p className="text-2xl font-bold text-surface-100">
              ${defiPortfolio.currentValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-sm text-surface-500">
              {defiPortfolio.positionsCount} posição(ões) ativa(s)
            </p>
            {defiPortfolio.totalEarnings > 0 && (
              <p className="text-sm text-green-400 mt-2">
                +${defiPortfolio.totalEarnings.toFixed(2)} rendimentos
              </p>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              className="mt-4 w-full"
              onClick={() => navigate('/banking/defi/pools')}
            >
              Ver Pools DeFi
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Card>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onClick={() => setShowBuyModal(true)}
          className="p-4 rounded-xl bg-surface-800/50 hover:bg-surface-800 border border-surface-700 hover:border-primary-500/50 transition-all group"
        >
          <ArrowDownLeft className="w-8 h-8 text-green-400 mb-2 group-hover:scale-110 transition-transform" />
          <p className="text-surface-100 font-medium">Comprar</p>
          <p className="text-surface-500 text-sm">Adquira criptomoedas</p>
        </motion.button>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          onClick={() => setShowSellModal(true)}
          className="p-4 rounded-xl bg-surface-800/50 hover:bg-surface-800 border border-surface-700 hover:border-primary-500/50 transition-all group"
        >
          <ArrowUpRight className="w-8 h-8 text-red-400 mb-2 group-hover:scale-110 transition-transform" />
          <p className="text-surface-100 font-medium">Vender</p>
          <p className="text-surface-500 text-sm">Converta para BRL</p>
        </motion.button>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          onClick={() => navigate('/banking/defi/pools')}
          className="p-4 rounded-xl bg-surface-800/50 hover:bg-surface-800 border border-surface-700 hover:border-primary-500/50 transition-all group"
        >
          <Layers className="w-8 h-8 text-purple-400 mb-2 group-hover:scale-110 transition-transform" />
          <p className="text-surface-100 font-medium">Pools DeFi</p>
          <p className="text-surface-500 text-sm">Invista e ganhe</p>
        </motion.button>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          onClick={() => navigate('/banking/crypto/marketplace')}
          className="p-4 rounded-xl bg-surface-800/50 hover:bg-surface-800 border border-surface-700 hover:border-primary-500/50 transition-all group"
        >
          <Users className="w-8 h-8 text-blue-400 mb-2 group-hover:scale-110 transition-transform" />
          <p className="text-surface-100 font-medium">Marketplace P2P</p>
          <p className="text-surface-500 text-sm">Compre de outros</p>
        </motion.button>
      </div>

      {/* Quotations Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-surface-100">Cotações em Tempo Real</h2>
          <Badge variant="info" size="sm">
            <RefreshCw className="w-3 h-3 mr-1" />
            Atualiza a cada 30s
          </Badge>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quotations.map((quote, index) => (
            <QuotationCard key={quote.symbol || quote.currency || index} quote={quote} index={index} />
          ))}
        </div>
      </div>

      {/* Wallets Section */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-surface-100">Suas Carteiras</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate('/banking/crypto/wallets')}>
            <Plus className="w-4 h-4 mr-1" />
            Nova Carteira
          </Button>
        </div>
        
        {wallets.length === 0 ? (
          <div className="text-center py-8">
            <Wallet className="w-12 h-12 mx-auto mb-4 text-surface-500 opacity-50" />
            <p className="text-surface-400">Nenhuma carteira encontrada</p>
            <p className="text-sm text-surface-500 mt-1">
              Compre criptomoedas para criar sua primeira carteira
            </p>
            <Button variant="secondary" size="sm" className="mt-4" onClick={() => setShowBuyModal(true)}>
              Comprar Cripto
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {wallets.slice(0, 4).map((wallet, index) => (
              <motion.div
                key={wallet.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center justify-between p-4 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${CRYPTO_COLORS[wallet.network] || 'from-surface-500 to-surface-600'} flex items-center justify-center text-white font-bold`}>
                    {CRYPTO_ICONS[wallet.network] || wallet.network[0]}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-surface-100 font-medium">{wallet.network}</p>
                      {wallet.label && (
                        <Badge variant="info" size="sm">{wallet.label}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="text-xs text-surface-500 bg-surface-800 px-2 py-0.5 rounded">
                        {wallet.address.slice(0, 10)}...{wallet.address.slice(-8)}
                      </code>
                      <button
                        onClick={() => handleCopyAddress(wallet.address)}
                        className="p-1 hover:bg-surface-700 rounded"
                      >
                        {copied === wallet.address ? (
                          <CheckCircle className="w-3 h-3 text-green-400" />
                        ) : (
                          <Copy className="w-3 h-3 text-surface-500" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-surface-100">
                    {wallet.balance.toFixed(8)}
                  </p>
                  <p className="text-sm text-surface-500">
                    ≈ ${wallet.balanceUsd.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </motion.div>
            ))}
            {wallets.length > 4 && (
              <Button variant="ghost" className="w-full" onClick={() => navigate('/banking/crypto/wallets')}>
                Ver todas as carteiras ({wallets.length})
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Recent Operations */}
      {recentOperations.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-surface-100">Operações Recentes</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/banking/crypto/history')}>
              Ver todas
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="space-y-2">
            {recentOperations.map((op) => (
              <div
                key={op.id}
                className="flex items-center justify-between p-3 rounded-lg bg-surface-800/50"
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center',
                    op.operationType === 'PURCHASE' 
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  )}>
                    {op.operationType === 'PURCHASE' ? (
                      <ArrowDownLeft className="w-5 h-5" />
                    ) : (
                      <ArrowUpRight className="w-5 h-5" />
                    )}
                  </div>
                  <div>
                    <p className="text-surface-100 font-medium">
                      {op.operationType === 'PURCHASE' ? 'Compra' : 'Venda'} {op.cryptoSymbol}
                    </p>
                    <p className="text-xs text-surface-500">
                      {new Date(op.createdAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={cn(
                    'font-semibold',
                    op.operationType === 'PURCHASE' ? 'text-green-400' : 'text-red-400'
                  )}>
                    {op.operationType === 'PURCHASE' ? '+' : '-'}{op.amountCrypto} {op.cryptoSymbol}
                  </p>
                  <p className="text-sm text-surface-500">
                    {formatCurrency(op.amountBrl / 100)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Modals */}
      <CryptoBuyModal
        isOpen={showBuyModal}
        onClose={() => setShowBuyModal(false)}
        virtualAccountId={virtualAccountId}
        quotations={quotations}
      />
      
      <CryptoSellModal
        isOpen={showSellModal}
        onClose={() => setShowSellModal(false)}
        virtualAccountId={virtualAccountId}
        wallets={wallets}
        quotations={quotations}
      />
    </div>
  );
}

// Quotation Card Component
function QuotationCard({ quote, index }: { quote: CryptoQuotation; index: number }) {
  // Use symbol as primary, fallback to currency for backward compatibility
  const cryptoSymbol = quote.symbol || quote.currency || 'UNKNOWN';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 + index * 0.05 }}
    >
      <Card className="hover:border-primary-500/30 transition-colors cursor-pointer">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${CRYPTO_COLORS[cryptoSymbol] || 'from-surface-500 to-surface-600'} flex items-center justify-center text-white font-bold`}>
            {CRYPTO_ICONS[cryptoSymbol] || cryptoSymbol[0]}
          </div>
          <div>
            <p className="text-surface-100 font-medium">{cryptoSymbol}</p>
            <p className="text-xs text-surface-500">
              {cryptoSymbol === 'BTC' ? 'Bitcoin' :
               cryptoSymbol === 'ETH' ? 'Ethereum' :
               cryptoSymbol === 'USDT' ? 'Tether' :
               cryptoSymbol === 'USDC' ? 'USD Coin' :
               cryptoSymbol === 'SOL' ? 'Solana' :
               cryptoSymbol === 'BNB' ? 'BNB' : cryptoSymbol}
            </p>
          </div>
        </div>
        <p className="text-xl font-bold text-surface-100">
          ${quote.priceUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
        <p className="text-sm text-surface-500">
          {formatCurrency(quote.priceBrl)}
        </p>
        <div className={cn(
          'flex items-center gap-1 text-sm mt-2',
          quote.change24h >= 0 ? 'text-green-400' : 'text-red-400'
        )}>
          {quote.change24h >= 0 ? (
            <TrendingUp className="w-4 h-4" />
          ) : (
            <TrendingDown className="w-4 h-4" />
          )}
          {quote.change24h >= 0 ? '+' : ''}{quote.change24h.toFixed(2)}%
        </div>
      </Card>
    </motion.div>
  );
}

export default BankingCrypto;
